import {Control as OLControl} from "ol/control";
import type {Coordinate} from "ol/coordinate";
import type Point from "ol/geom/Point";
import type Layer from "ol/layer/Layer";
import type OlMap from "ol/Map";
import type VectorSource from "ol/source/Vector";

/** Ein mit dem REVA-Layer-Key getaggter Layer (siehe util/maps.jsx, ATTR_KEY). */
type RevaLayer = Layer & {reva_key?: string};

type PinControlOptions = {
    /** Tooltip des Buttons */
    title?: string;
    /** Default-Zoom, auf den beim Klick zurückgesprungen wird */
    zoom?: number;
    /** Default-Resolution (alternativ zum Zoom, vgl. createView) */
    resolution?: number;
};

/**
 * Karten-Control links oben: erscheint, sobald die Karte vom Default abweicht — also gezoomt
 * ODER gepannt wurde (aktuelle Mitte ≠ Adress-Marker oder aktueller Zoom/Auflösung ≠ Default).
 * Ein Klick zentriert die Karte wieder auf den Marker mit dem Default-Zoom der jeweiligen Karte.
 */
export default class OpenLayersPinControl extends OLControl {
    private readonly zoom?: number;
    private readonly resolution?: number;

    constructor(options: PinControlOptions = {}) {
        const button = document.createElement("button");
        button.innerHTML = '<span class="pi pi-map-marker"></span>';
        if (options.title) button.title = options.title;

        const element = document.createElement("div");
        element.className = "ol-unselectable ol-control pin-button";
        element.style.display = "none";
        element.appendChild(button);

        super({element});
        this.zoom = options.zoom;
        this.resolution = options.resolution;
        button.addEventListener("click", () => this.recenter());
    }

    setMap(map: OlMap | null): void {
        super.setMap(map);
        if (map) {
            map.on("moveend", () => this.updateVisibility());
            // Initialzustand, sobald Layer/Features gerendert sind
            map.once("postrender", () => this.updateVisibility());
        }
    }

    private getMarkerCoordinate(): Coordinate | null {
        const map = this.getMap();
        const markerLayer = map?.getAllLayers().find((l) => (l as RevaLayer).reva_key === "markers") as
            | Layer<VectorSource>
            | undefined;
        const feature = markerLayer?.getSource()?.getFeatures()?.[0];
        const geometry = feature?.getGeometry() as Point | undefined;
        return geometry?.getCoordinates?.() ?? null;
    }

    private updateVisibility(): void {
        const map = this.getMap();
        const coordinate = this.getMarkerCoordinate();
        if (!map || !coordinate) {
            this.element!.style.display = "none";
            return;
        }
        const view = map.getView();
        const center = view.getCenter();
        const resolution = view.getResolution();
        // Gepannt? Aktuelle Mitte weicht vom Marker ab (Toleranz ~ wenige Pixel).
        const tolerance = (resolution ?? 0) * 3;
        const panned =
            !center ||
            Math.abs(center[0] - coordinate[0]) > tolerance ||
            Math.abs(center[1] - coordinate[1]) > tolerance;
        // Gezoomt? Aktuelle Auflösung/Zoom weicht vom Default der Karte ab.
        let zoomed = false;
        if (this.resolution != null && resolution != null) {
            zoomed = Math.abs(resolution - this.resolution) / this.resolution > 0.01;
        } else if (this.zoom != null) {
            const z = view.getZoom();
            zoomed = z != null && Math.abs(z - this.zoom) > 0.01;
        }
        this.element!.style.display = panned || zoomed ? "" : "none";
    }

    private recenter(): void {
        const map = this.getMap();
        const coordinate = this.getMarkerCoordinate();
        if (!map || !coordinate) return;
        const animation: {center: Coordinate; duration: number; zoom?: number; resolution?: number} = {
            center: coordinate,
            duration: 500,
        };
        if (this.resolution != null) animation.resolution = this.resolution;
        else if (this.zoom != null) animation.zoom = this.zoom;
        map.getView().animate(animation);
    }
}
